import React, { useEffect, useRef, createElement } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { FloatingOrbs } from './FloatingOrbs';
import { MapPin, Users, Clock } from 'lucide-react';
const locations = [{
  name: 'Singapore',
  flag: '🇸🇬',
  region: 'Southeast Asia'
}, {
  name: 'Philippines',
  flag: '🇵🇭',
  region: 'Southeast Asia'
}, {
  name: 'Brisbane',
  flag: '🇦🇺',
  region: 'Australia'
}, {
  name: 'Gold Coast',
  flag: '🇦🇺',
  region: 'Australia'
}, {
  name: 'Sydney',
  flag: '🇦🇺',
  region: 'Australia'
}, {
  name: 'Melbourne',
  flag: '🇦🇺',
  region: 'Australia'
}, {
  name: 'Perth',
  flag: '🇦🇺',
  region: 'Australia'
}];
export const Contact: React.FC = () => {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://server.fillout.com/embed/v1/';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);
  return <section id="contact" className="relative py-24 md:py-32 overflow-hidden bg-deep-navy/50" data-id="element-242">
      <FloatingOrbs count={10} data-id="element-243" />

      {/* Background glows */}
      <motion.div className="absolute top-0 left-0 right-0 h-[40vh] pointer-events-none" style={{
      background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(250, 255, 49, 0.08) 0%, transparent 70%)',
      opacity: 0.6
    }} data-id="element-244" />

      <motion.div className="absolute bottom-0 left-0 right-0 h-[50vh] pointer-events-none" style={{
      background: 'radial-gradient(ellipse 100% 60% at 50% 100%, rgba(250, 255, 49, 0.12) 0%, transparent 60%)',
      opacity: 0.6
    }} data-id="element-245" />

      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-246">
        {/* Section Title */}
        <motion.div className="text-center mb-16" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} viewport={{
        once: true
      }} data-id="element-247">
          <motion.h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold tracking-wider mb-4 text-white" initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6
        }} viewport={{
          once: true
        }} data-id="element-248">
            Let's build your{' '}
            <span className="text-electric-yellow" data-id="element-249">intelligent system</span>
          </motion.h2>
          <motion.p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto" initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} viewport={{
          once: true
        }} data-id="element-250">
            Get in touch to discuss your system design and delivery strategy
            across CRM, ERP, AI, and bespoke solutions
          </motion.p>

          {/* Purple gradient divider */}
          <motion.div className="max-w-xs mx-auto mt-8" initial={{
          scaleX: 0,
          opacity: 0
        }} whileInView={{
          scaleX: 1,
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} viewport={{
          once: true
        }} data-id="element-251">
            <div className="h-[1px]" style={{
            background: 'linear-gradient(90deg, rgba(79, 12, 154, 0), rgba(79, 12, 154, 0.6), rgba(79, 12, 154, 0))'
          }} data-id="element-252" />
          </motion.div>

          {/* Fillout Popup Embed */}
          <motion.div className="mt-6 flex justify-center" initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} viewport={{
          once: true
        }} data-id="element-253">
            <div data-fillout-id="oxJy4k63Bfus" data-fillout-embed-type="popup" data-fillout-button-text="Contact Us" data-fillout-dynamic-resize data-fillout-inherit-parameters data-fillout-popup-size="medium" data-id="element-254" />
          </motion.div>
        </motion.div>

        {/* Presence Section */}
        <motion.div className="max-w-6xl mx-auto" initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} viewport={{
        once: true
      }} data-id="element-255">
          {/* Section Title */}
          <motion.div className="text-center mb-12" initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} viewport={{
          once: true
        }} data-id="element-256">
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-2" data-id="element-257">
              Our Presence
            </h3>
            <p className="text-white/70 text-sm md:text-base" data-id="element-258">
              Supporting organisations across Asia-Pacific
            </p>
          </motion.div>

          {/* Unified responsive grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5 auto-rows-fr" data-id="element-259">
            {locations.map((location, index) => <motion.div key={location.name} className="relative rounded-xl border backdrop-blur-sm p-5 md:p-6 flex flex-col justify-between min-h-[140px] md:min-h-[160px]" style={{
            backgroundColor: 'rgba(79, 12, 154, 0.10)',
            borderColor: 'rgba(255, 255, 255, 0.10)',
            boxShadow: '0 0 24px rgba(250, 255, 49, 0.06), inset 0 0 0 1px rgba(255,255,255,0.02)'
          }} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: index * 0.06
          }} viewport={{
            once: true
          }} whileHover={{
            y: -3,
            borderColor: 'rgba(250, 255, 49, 0.25)',
            boxShadow: '0 0 28px rgba(250, 255, 49, 0.12), inset 0 0 0 1px rgba(255,255,255,0.04)'
          }} data-id="element-260">
                {/* Top row: flag + pulse dot */}
                <div className="flex items-start justify-between mb-3" data-id="element-261">
                  <span className="text-2xl md:text-3xl leading-none" data-id="element-262">
                    {location.flag}
                  </span>
                  <motion.div className="w-1.5 h-1.5 rounded-full mt-2" style={{
                backgroundColor: '#faff31',
                boxShadow: '0 0 8px #faff31'
              }} animate={{
                scale: [1, 1.4, 1],
                opacity: [0.6, 1, 0.6]
              }} transition={{
                duration: 2.4,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: index * 0.3
              }} data-id="element-263" />
                </div>

                {/* City + region */}
                <div data-id="element-264">
                  <h4 className="text-lg md:text-xl font-heading font-bold text-white leading-tight mb-1" data-id="element-265">
                    {location.name}
                  </h4>
                  <p className="text-xs md:text-sm text-white/60" data-id="element-266">
                    {location.region}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </motion.div>
      </div>
    </section>;
};